import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  HostListener
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { slideDownAnimation } from '../task-animations';

/**
 * Interface for milestone data
 */
export interface Milestone {
  id?: string | number;
  name: string;
  status?: string;
}

/**
 * TaskHeaderComponent
 *
 * Header component for task dialog/drawer matching prototype design.
 * Features:
 * - Title with status badge
 * - Project and milestone info display
 * - Action buttons (search, fullscreen, save, close, delete)
 * - Animated search bar
 * - Keyboard shortcuts (Ctrl+S for save)
 *
 * Prototype Reference: TaskEditDrawer.tsx (lines 885-1007)
 *
 * @selector app-task-header
 * @changeDetection OnPush
 */
@Component({
    selector: 'app-task-header',
    templateUrl: './task-header.component.html',
    styleUrls: ['./task-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [slideDownAnimation],
    standalone: false
})
export class TaskHeaderComponent implements OnInit, OnDestroy {

  // ============================================================================
  // INPUT PROPERTIES
  // ============================================================================

  /**
   * Edit mode flag - determines if creating new task or editing existing
   */
  @Input() isEdit: boolean = false;

  /**
   * Task title from user input (dynamically updates as user types)
   */
  @Input() taskTitle: string = '';

  /**
   * Module label (e.g., 'Job', 'Proposal', 'Purchase Order')
   */
  @Input() moduleLabel: string = 'Project';

  /**
   * Project name to display in header
   */
  @Input() projectName: string = '';

  /**
   * Project ID to display in header
   */
  @Input() projectId: string | number = '';

  /**
   * Route to the linked module record's detail page (e.g. '/bids/bid/194').
   * When set, the project name/id render as a link. When null (module
   * unmapped or record data unavailable), they render as plain text as before.
   */
  @Input() recordRoute: string | null = null;

  /**
   * Current task status (e.g., 'new', 'in-progress', 'completed')
   */
  @Input() status: string = '';

  /**
   * Status text to display in badge (optional, defaults to status if not provided)
   */
  @Input() statusText: string = '';

  /**
   * Associated milestone information
   */
  @Input() milestone?: Milestone;

  /**
   * Fullscreen mode state (controlled by parent)
   */
  @Input() isFullscreen: boolean = false;

  /**
   * Form validity state - controls save button disabled state
   */
  @Input() isFormValid: boolean = true;

  /**
   * Search query value (controlled by parent)
   */
  @Input() searchQuery: string = '';

  /**
   * Task templates array
   */
  @Input() templates: any[] = [];

  /**
   * Flag indicating if templates are available
   */
  @Input() hasTemplates: boolean = false;

  /**
   * Task stages array
   */
  @Input() stages: any[] = [];

  /**
   * Currently selected stage
   */
  @Input() selectedStage: any = {};

  /**
   * Flag indicating if stages are available
   */
  @Input() hasStages: boolean = false;

  /**
   * High priority flag
   */
  @Input() isHighPriority: boolean = false;

  /**
   * Numeric priority value (1=Low, 2=Medium, 3=High, 4=Urgent)
   */
  @Input() priority: number = 2;

  /**
   * View mode flag - adapts UI for view-only display
   */
  @Input() isViewMode: boolean = false;

  /**
   * Available statuses list
   */
  @Input() statuses: any[] = [];

  /**
   * Currently selected status label
   */
  @Input() selectedStatus: any = null;

  /**
   * Flag indicating if statuses are available
   */
  @Input() hasStatuses: boolean = false;

  /**
   * Show status dropdown in header
   */
  @Input() showStatusDropdown: boolean = false;

  /**
   * Show mark complete button
   */
  @Input() showMarkComplete: boolean = false;

  /**
   * Show copy button
   */
  @Input() showCopy: boolean = false;

  /**
   * Show PDF export button
   */
  @Input() showPdfExport: boolean = false;

  /**
   * Show delete button
   */
  @Input() showDelete: boolean = false;

  /**
   * Permission for mark complete action
   */
  @Input() markCompletePermission: string = 'DONOTAPPLY';

  /**
   * Permission for PDF export action
   */
  @Input() pdfExportPermission: string = 'DONOTAPPLY';

  /**
   * Permission for copy task action
   */
  @Input() copyPermission: string = 'DONOTAPPLY';

  /**
   * Permission for delete action
   */
  @Input() deletePermission: string = 'DONOTAPPLY';

  /**
   * Permission for edit action (view mode)
   */
  @Input() editPermission: string = 'DONOTAPPLY';

  /**
   * Lock state - disables all edit/delete actions when true
   */
  @Input() isLocked: boolean = false;

  /**
   * Permission for status change action
   */
  @Input() statusChangePermission: string = 'DONOTAPPLY';

  /**
   * Permission for stage change action
   */
  @Input() stageChangePermission: string = 'DONOTAPPLY';

  /** Text the task was flagged with; empty = not flagged. */
  @Input() flagText: string = '';

  /** Show the flag button (only the task panels that save the flag turn this on). */
  @Input() showFlag: boolean = false;

  // ============================================================================
  // OUTPUT EVENTS
  // ============================================================================

  /**
   * Emits when save button is clicked
   */
  @Output() save = new EventEmitter<void>();

  /**
   * Emits when close button is clicked
   */
  @Output() close = new EventEmitter<void>();

  /**
   * Emits when the linked module record (name or #id) is clicked, so the
   * parent can close this task's own dialog/panel before the record's
   * detail page finishes navigating in behind it.
   */
  @Output() recordLinkClick = new EventEmitter<void>();

  /**
   * Emits when delete button is clicked (edit mode only)
   */
  @Output() delete = new EventEmitter<void>();

  /**
   * Emits when search is toggled
   */
  @Output() searchToggle = new EventEmitter<boolean>();

  /**
   * Emits when search input changes
   */
  @Output() searchChange = new EventEmitter<string>();

  /**
   * Emits when fullscreen is toggled
   */
  @Output() fullscreenToggle = new EventEmitter<boolean>();

  /**
   * Emits when a template is selected
   */
  @Output() templateSelect = new EventEmitter<any>();

  /**
   * Emits when a stage is selected
   */
  @Output() stageSelect = new EventEmitter<any>();

  /**
   * Emits when priority toggle is clicked
   */
  @Output() priorityToggle = new EventEmitter<void>();

  /**
   * Emits when status is selected from dropdown
   */
  @Output() statusSelect = new EventEmitter<any>();

  /**
   * Emits when mark complete button is clicked
   */
  @Output() markComplete = new EventEmitter<void>();

  /**
   * Emits when copy button is clicked
   */
  @Output() copy = new EventEmitter<void>();

  /**
   * Emits when PDF export button is clicked
   */
  @Output() pdfExport = new EventEmitter<void>();

  /**
   * Emits when edit button is clicked
   */
  @Output() edit = new EventEmitter<void>();

  /**
   * Emits when lock/unlock button is clicked
   */
  @Output() lockToggle = new EventEmitter<void>();

  /**
   * Emits when Save is clicked in the flag popup: the new state (true = flag, false = unflag) and the optional
   * reason (empty string when none was typed). Nothing is emitted by merely clicking the flag button.
   */
  @Output() flagSave = new EventEmitter<{ isFlagged: boolean; reason: string }>();

  // ============================================================================
  // VIEW CHILDREN
  // ============================================================================

  /**
   * Reference to search input element for auto-focus
   */
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('flagTrigger') flagTrigger?: MatMenuTrigger;

  /** Text being typed in the flag popup. */
  flagDraft = '';

  /** True while the task is flagged. */
  @Input() flagged: boolean = false;

  /** True when the popup was opened to REMOVE the flag (the task is flagged now); it changes the wording and what Save does. */
  flagPopupUnflagged = false;

  /** Click only notes which way the flag will go; the popup (opened by the menu trigger) does the rest, and nothing is saved until Save. */
  onFlagClick(): void {
    this.flagPopupUnflagged = this.flagged;
  }

  /** Start every popup with an empty box: a reason is optional. */
  onFlagMenuOpened(): void {
    this.flagDraft = '';
  }

  /** Save flags / unflags the task, with the reason if one was typed. Cancel just closes the popup and changes nothing. */
  saveFlag(): void {
    this.flagSave.emit({ isFlagged: !this.flagPopupUnflagged, reason: this.flagDraft.trim() });
    this.flagTrigger?.closeMenu();
  }

  // ============================================================================
  // COMPONENT STATE
  // ============================================================================

  /**
   * Internal search bar visibility state
   */
  public isSearchOpen: boolean = false;

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  constructor() {}

  ngOnInit(): void {
    // Set default status text if not provided
    if (!this.statusText && this.status) {
      this.statusText = this.status.replace('-', ' ').toUpperCase();
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  // ============================================================================
  // KEYBOARD SHORTCUTS
  // ============================================================================

  /**
   * Handle keyboard shortcuts
   * Ctrl+S / Cmd+S: Save task
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    // Ctrl+S or Cmd+S: Save
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      this.onSaveClick();
    }
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Toggle search bar visibility
   */
  public toggleSearch(): void {
    this.isSearchOpen = !this.isSearchOpen;
    this.searchToggle.emit(this.isSearchOpen);

    // Auto-focus search input when opened
    if (this.isSearchOpen) {
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 250); // Wait for animation to complete
    }
  }

  /**
   * Toggle fullscreen mode
   */
  public toggleFullscreen(): void {
    this.fullscreenToggle.emit(!this.isFullscreen);
  }

  /**
   * Handle save button click
   */
  public onSaveClick(): void {
    if (this.isFormValid) {
      this.save.emit();
    }
  }

  /**
   * Handle close button click
   */
  public onCloseClick(): void {
    this.close.emit();
  }

  /**
   * Handle delete button click
   */
  public onDeleteClick(): void {
    if (this.isLocked) {
      return;
    }
    this.delete.emit();
  }

  /**
   * Handle search input change
   */
  public onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchChange.emit(input.value);
  }

  /**
   * Get formatted status text for display
   * Always uses current status input to reflect real-time changes
   * @returns Formatted status string
   */
  public getFormattedStatus(): string {
    // Use selectedStatus object if available (contains correct name)
    if (this.selectedStatus && this.selectedStatus.name) {
      return this.selectedStatus.name.replace('-', ' ').toUpperCase();
    }

    // Fallback to status string (for backward compatibility)
    if (!this.status || typeof this.status !== 'string') {
      return 'NEW';
    }
    return this.status.replace('-', ' ').toUpperCase();
  }

  /**
   * Check if selected status is locked
   * Returns true if the status has lock_status = true
   * @returns Boolean indicating if status is locked
   */
  public getSelectedStatusLocked(): boolean {
    // Check if selectedStatus has lock_status (handles both true and 1)
    if (this.selectedStatus && (this.selectedStatus.lock_status === true || this.selectedStatus.lock_status === 1)) {
      return true;
    }

    // Fallback: Try to find status in statuses array by status string
    if (this.statuses && this.statuses.length > 0 && this.status) {
      const normalizedStatus = this.status.toLowerCase().trim();
      const statusObj = this.statuses.find((s: any) =>
        s.name?.toLowerCase().trim() === normalizedStatus
      );
      if (statusObj && (statusObj.lock_status === true || statusObj.lock_status === 1)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Convert numeric priority to display text
   * @returns Priority text ('low', 'medium', 'high', 'urgent')
   */
  public getPriorityText(): string {
    const priorityMap: { [key: number]: string } = {
      1: 'low',
      2: 'medium',
      3: 'high',
      4: 'urgent'
    };
    return priorityMap[this.priority] || 'medium';
  }

  /**
   * Handle template selection
   * @param template Selected template
   */
  public onTemplateSelect(template: any): void {
    this.templateSelect.emit(template);
  }

  /**
   * Handle stage selection
   * @param stage Selected stage
   */
  public onStageSelect(stage: any): void {
    this.stageSelect.emit(stage);
  }

  /**
   * Get stage display name for tooltip
   * @returns Stage name or default text
   */
  public getStageDisplayName(): string {
    return this.selectedStage?.name || 'Select Stage';
  }

  /**
   * Handle priority toggle click
   */
  public onPriorityToggle(): void {
    this.priorityToggle.emit();
  }

  /**
   * Handle status selection
   * @param status Selected status
   */
  public onStatusSelect(status: any): void {
    this.statusSelect.emit(status);
  }

  /**
   * Handle mark complete click
   */
  public onMarkComplete(): void {
    this.markComplete.emit();
  }

  /**
   * Handle copy click
   */
  public onCopy(): void {
    if (this.isLocked) {
      return;
    }
    this.copy.emit();
  }

  /**
   * Handle PDF export click
   */
  public onPdfExport(): void {
    this.pdfExport.emit();
  }

  /**
   * Handle edit click
   */
  public onEdit(): void {
    if (this.isLocked) {
      return;
    }
    this.edit.emit();
  }

  /**
   * Handle lock toggle click
   */
  public onLockToggle(): void {
    this.lockToggle.emit();
  }
}
